import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter, Link, NavLink, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
// Styles imported in main.tsx
import { CardType, TodaySnapshot, getLatestSnapshot, saveSnapshot } from './db/indexedDb';
import { needsInviteCode, isAIWriting, isPublicDeploy } from './config/features';
import { hasValidInviteCode } from './config/inviteCodes';
import { InviteGate } from './components/InviteGate';
import { seedInitialData } from './db/seedData';
import { CARD_TYPE_ORDER, buildMixedQueue, getTodayPlan, snapshotFromPlan, type TodayPlanResult } from './srs/dashboard';
import SessionPage from './session/SessionPage';
import HistoryPage from './history/HistoryPage';
import HistoryDetailPage from './history/HistoryDetailPage';
import { clearPersistedSession } from './srs/sessionController';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { AIWritingPage } from './writing/aiWriting';
import { GoalPlannerPage } from './pages/GoalPlannerPage';
import { WeeklyGoalPage } from './pages/WeeklyGoalPage';
import { UpdatePrompt } from './pwa/UpdatePrompt';
import { NetworkErrorToast } from './components/NetworkErrorToast';
import { WritingModeSwitcher } from './components/WritingModeSwitcher';
import { SrsSettingsSection } from './components/SrsSettingsSection';

// Shadow Reading Module
import { ShadowReadingPage } from './speaking/shadowReading/pages/ShadowReadingPage';
import { ShadowPracticePage } from './speaking/shadowReading/pages/ShadowPracticePage';
import { RetellingPage } from './speaking/shadowReading/pages/RetellingPage';
import { AIDialogPage } from './speaking/aiDialog';

// Topic Dictation Module
import { TopicDictationPage } from './writing/topicDictation';

const navItems = [
  { to: '/today', label: '今日', icon: '◉' },
  { to: '/stats', label: '统计', icon: '▤' },
  { to: '/settings', label: '设置', icon: '⚙' },
];

const skillMeta: Record<CardType, { name: string }> = {
  speaking: { name: '口语' },
  writing: { name: '写作' },
  listening: { name: '听力' },
  vocab: { name: '词汇' },
};

const formatDateTime = (timestamp?: number) => {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleString();
};

type DashboardState = {
  loading: boolean;
  error?: string;
  data?: TodayPlanResult;
  snapshot?: TodaySnapshot;
  offline: boolean;
  source: 'live' | 'snapshot' | 'none';
};

function useTodayDashboard(): DashboardState {
  const [state, setState] = useState<DashboardState>({
    loading: true,
    offline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
    source: 'live',
  });

  useEffect(() => {
    let active = true;
    const initialOffline = typeof navigator !== 'undefined' ? !navigator.onLine : false;
    (async () => {
      try {
        await seedInitialData();
        const data = await getTodayPlan();
        if (!active) return;
        const snapshot = snapshotFromPlan(data.plan, Date.now(), 'today dashboard refresh');
        await saveSnapshot(snapshot);
        setState({ loading: false, data, snapshot, offline: initialOffline, source: 'live' });
      } catch (error) {
        if (!active) return;
        const message = error instanceof Error ? error.message : '加载今日任务失败';
        const offlineFlag = initialOffline || (typeof navigator !== 'undefined' ? !navigator.onLine : false);
        const snapshot = await getLatestSnapshot().catch(() => undefined);
        if (snapshot) {
          setState({ loading: false, snapshot, offline: offlineFlag, source: 'snapshot', error: undefined });
        } else {
          console.warn('[today] missing cached resource: todayTaskSummary');
          setState({ loading: false, offline: offlineFlag, source: 'none', error: message });
        }
      }
    })();

    const updateOffline = () =>
      setState((prev) => ({
        ...prev,
        offline: typeof navigator !== 'undefined' ? !navigator.onLine : prev.offline,
      }));
    window.addEventListener('online', updateOffline);
    window.addEventListener('offline', updateOffline);

    return () => {
      active = false;
      window.removeEventListener('online', updateOffline);
      window.removeEventListener('offline', updateOffline);
    };
  }, []);

  return state;
}

function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const isSessionPage = location.pathname.startsWith('/session') || location.pathname.startsWith('/ai-writing');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('enpass_sidebar_collapsed') === 'true';
  });

  // 保存最后的session路径
  useEffect(() => {
    if (isSessionPage) {
      sessionStorage.setItem('lastSessionPath', location.pathname);
    }
  }, [location.pathname, isSessionPage]);

  // 切换侧边栏
  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('enpass_sidebar_collapsed', String(newState));
  };

  // 同步侧边栏状态到 body（供 session-layout 使用）
  useEffect(() => {
    document.body.classList.toggle('sidebar-collapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  const handleTodayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const lastSession = sessionStorage.getItem('lastSessionPath');
    if (lastSession && location.pathname !== lastSession) {
      navigate(lastSession);
    } else {
      navigate('/today');
    }
  };

  // 获取当前页面标题
  const getPageTitle = () => {
    if (location.pathname === '/today') return 'enPASS';
    if (location.pathname === '/stats') return 'enPASS · Stats';
    if (location.pathname === '/settings') return 'enPASS · Settings';
    return 'enPASS';
  };

  const renderNavItems = (className: string) => (
    navItems.map((item) => {
      const isTodayActive = item.to === '/today' && (location.pathname === '/today' || isSessionPage);
      const isOtherActive = item.to !== '/today' && location.pathname === item.to;
      const isActive = isTodayActive || isOtherActive;

      if (item.to === '/today') {
        return (
          <a
            key={item.to}
            href={item.to}
            onClick={handleTodayClick}
            className={`${className} ${isActive ? 'active' : ''}`}
          >
            <span className="nav-icon" aria-hidden="true">{item.icon}</span>
            <span className="nav-label">{item.label}</span>
          </a>
        );
      }

      return (
        <NavLink
          key={item.to}
          to={item.to}
          className={`${className} ${isActive ? 'active' : ''}`}
        >
          <span className="nav-icon" aria-hidden="true">{item.icon}</span>
          <span className="nav-label">{item.label}</span>
        </NavLink>
      );
    })
  );

  return (
    <div className={`app-shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* 桌面端：侧边栏导航 */}
      <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">{sidebarCollapsed ? 'E' : 'enPASS'}</div>
          <button className="sidebar-toggle" onClick={toggleSidebar} title={sidebarCollapsed ? '展开' : '收起'}>
            {sidebarCollapsed ? '→' : '←'}
          </button>
        </div>
        <nav className="sidebar-nav">
          {renderNavItems('sidebar-item')}
        </nav>
      </aside>

      {/* 移动端：顶部状态栏 */}
      {!isSessionPage && (
        <header className="top-bar">
          <span className="top-bar-title">{getPageTitle()}</span>
        </header>
      )}

      <main className="main-content">
        <div className="container">
          <Outlet />
        </div>
      </main>

      {/* 移动端：底部导航 */}
      <nav className="bottom-nav">
        {renderNavItems('nav-item')}
      </nav>
    </div>
  );
}

function TodayPage() {
  const navigate = useNavigate();

  // 进入 TodayPage 时清除 session 状态（表示用户主动回到主页）
  useEffect(() => {
    sessionStorage.removeItem('lastSessionPath');
    // 用户主动回到主页，清除 session 进度
    clearPersistedSession();
  }, []);

  const { data, error, loading, snapshot, source } = useTodayDashboard();
  const plan = data?.plan ?? (snapshot ? { skills: snapshot.skills, totals: snapshot.totals } : undefined);
  const limits = data?.limits;
  const mixedQueue = data?.plan ? buildMixedQueue(data.plan) : [];

  // 周目标数据（新版）
  const [weeklyData, setWeeklyData] = useState<{
    hasGoal: boolean;
    targetHours: number;
    actualHours: number;
    progressPercent: number;
    cardsCompleted: number;
    bySkill: Record<string, number>;
  }>({ hasGoal: false, targetHours: 0, actualHours: 0, progressPercent: 0, cardsCompleted: 0, bySkill: {} });

  useEffect(() => {
    (async () => {
      const { getSettings, defaultDb, getWeeklyStudyStats, getWeekStart } = await import('./db/indexedDb');

      const settings = await getSettings('app', defaultDb);
      const weeklyGoal = settings?.weeklyGoal;
      const targetHours = weeklyGoal?.targetHoursPerWeek ?? 7;

      // 获取本周统计
      const stats = await getWeeklyStudyStats(getWeekStart(), defaultDb);
      const actualHours = Math.round(stats.totalMinutes / 6) / 10; // 保留一位小数
      const progressPercent = targetHours > 0
        ? Math.min(100, Math.round((actualHours / targetHours) * 100))
        : 0;

      setWeeklyData({
        hasGoal: !!weeklyGoal,
        targetHours,
        actualHours,
        progressPercent,
        cardsCompleted: stats.cardsCompleted,
        bySkill: stats.bySkill,
      });
    })();
  }, [data]);

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"><span></span></div>
      </div>
    );
  }

  return (
    <div className="today-fixed">
      {/* Page Header + Weekly Progress */}
      <header className="page-header">
        <div className="page-header-text">
          <h1 className="page-title">TODAY'S MISSION</h1>
          <p className="page-subtitle goal-subtitle" onClick={() => navigate('/goal')}>
            本周 {weeklyData.actualHours}h / {weeklyData.targetHours}h · {weeklyData.progressPercent}%
            <span className="goal-link">目标→</span>
          </p>
        </div>
        {/* 桌面端：周进度条 */}
        <div className="stats-bar stats-bar-desktop" onClick={() => navigate('/goal')} style={{ cursor: 'pointer' }}>
          <div className="weekly-progress-mini">
            <div className="weekly-progress-bar-mini">
              <div
                className="weekly-progress-fill-mini"
                style={{ width: `${weeklyData.progressPercent}%` }}
              />
            </div>
            <span className="weekly-progress-text-mini">
              {weeklyData.actualHours}h / {weeklyData.targetHours}h
            </span>
          </div>
        </div>
      </header>

      {/* 移动端：周进度条 */}
      <div className="weekly-progress-mobile" onClick={() => navigate('/goal')} style={{ cursor: 'pointer' }}>
        <div className="weekly-progress-header">
          <span>本周进度</span>
          <span>{weeklyData.cardsCompleted} 张卡片</span>
        </div>
        <div className="weekly-progress-bar-mobile">
          <div
            className="weekly-progress-fill-mobile"
            style={{ width: `${weeklyData.progressPercent}%` }}
          />
        </div>
        <div className="weekly-progress-footer">
          <span>{weeklyData.actualHours}h / {weeklyData.targetHours}h</span>
          <span>{weeklyData.progressPercent}%</span>
        </div>
      </div>

      {/* Error Messages */}
      {error && (
        <div className="card">
          <div className="card-body" style={{ background: 'var(--accent-red)', color: '#000' }}>
            {error}
          </div>
        </div>
      )}

      {/* Offline Snapshot Notice */}
      {source === 'snapshot' && snapshot && (
        <div className="card">
          <div className="card-body" style={{ background: 'var(--accent-yellow)', color: '#000' }}>
            <strong>OFFLINE SNAPSHOT</strong>
            <p className="caption">Generated: {formatDateTime(snapshot.generatedAt)}</p>
          </div>
        </div>
      )}

      {/* Mixed Queue */}
      <section className="section">
        <h2 className="section-title">MIXED QUEUE</h2>
        <div className="card">
          <div className="card-body flex justify-between items-center">
            <div>
              <div className="h3">{mixedQueue.length} CARDS</div>
              <p className="caption">Cross-skill review queue</p>
            </div>
            <Link className="btn btn-primary" to="/session/mixed">
              START MIXED →
            </Link>
          </div>
        </div>
      </section>

      {/* Skill Cards */}
      <section className="section">
        <h2 className="section-title">SKILLS</h2>
        <div className="skill-grid">
          {CARD_TYPE_ORDER.map((type) => {
            const meta = skillMeta[type];
            // AI 写作模式下，写作跳转到 AI 写作页面
            const writingLink = type === 'writing' && isAIWriting ? '/ai-writing' : `/session/${type}`;
            const stats = plan?.skills.find((item) => item.type === type);
            const planned = stats?.plannedCount ?? 0;
            const rawLimit = stats?.limit ?? limits?.[type];
            const dueCount = stats?.dueCount ?? 0;
            const progress = rawLimit && rawLimit > 0 ? Math.min((planned / rawLimit) * 100, 100) : 0;

            // Cloudflare 版本的写作模块：使用切换器
            if (type === 'writing' && !isPublicDeploy) {
              return (
                <WritingModeSwitcher
                  key={type}
                  planned={planned}
                  limit={rawLimit ?? 0}
                  dueCount={dueCount}
                />
              );
            }

            return (
              <article className="skill-card" key={type}>
                <div className="skill-card-header">
                  <div className="skill-card-title">
                    <span className={`skill-dot ${type}`} />
                    <span>{meta.name.toUpperCase()}</span>
                  </div>
                  <div className="skill-card-count">{planned}/{rawLimit ?? 0}</div>
                </div>
                <div className="skill-card-progress">
                  <div className="progress">
                    <div className="progress-fill" style={{ width: `${progress}%` }} />
                  </div>
                  <span className="caption">DUE {dueCount}</span>
                </div>
                <Link className="btn btn-primary" to={writingLink}>
                  START →
                </Link>
              </article>
            );
          })}
        </div>
      </section>

      {/* Focused Practice - 专项练习入口 */}
      {!isPublicDeploy && (
        <section className="section">
          <h2 className="section-title">FOCUSED PRACTICE</h2>
          <div className="focused-practice-grid">
            {/* 话题默写入口 - SRS 模式 */}
            <Link to="/writing/topic-dictation" className="focused-practice-card">
              <div className="focused-practice-icon">📝</div>
              <div className="focused-practice-content">
                <div className="focused-practice-title">话题默写</div>
                <div className="focused-practice-desc">Simon 句库 · SRS 复习</div>
              </div>
              <div className="focused-practice-arrow">→</div>
            </Link>

            {/* 影子跟读入口 */}
            <Link to="/speaking/shadow" className="focused-practice-card">
              <div className="focused-practice-icon">🎤</div>
              <div className="focused-practice-content">
                <div className="focused-practice-title">影子跟读</div>
                <div className="focused-practice-desc">视频跟读 · Retelling</div>
              </div>
              <div className="focused-practice-arrow">→</div>
            </Link>

            {/* AI 对话入口 */}
            <Link to="/speaking/dialog" className="focused-practice-card">
              <div className="focused-practice-icon">💬</div>
              <div className="focused-practice-content">
                <div className="focused-practice-title">AI 对话</div>
                <div className="focused-practice-desc">自由口语 · 表达升级</div>
              </div>
              <div className="focused-practice-arrow">→</div>
            </Link>
          </div>
        </section>
      )}

    </div>
  );
}

function StatsPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<{
    totalCards: number;
    totalReviews: number;
    byType: Record<string, { cards: number; reviews: number }>;
    todayReviews: number;
    weekReviews: number;
  } | null>(null);

  useEffect(() => {
    (async () => {
      const { defaultDb } = await import('./db/indexedDb');
      const cards = await defaultDb.cards.toArray();
      const now = Date.now();
      const todayStart = new Date(now).setHours(0, 0, 0, 0);
      const weekStart = todayStart - 6 * 24 * 60 * 60 * 1000;

      const byType: Record<string, { cards: number; reviews: number }> = {};
      let totalReviews = 0;
      let todayReviews = 0;
      let weekReviews = 0;

      for (const card of cards) {
        if (!byType[card.type]) byType[card.type] = { cards: 0, reviews: 0 };
        byType[card.type].cards += 1;
        byType[card.type].reviews += card.history.length;
        totalReviews += card.history.length;

        for (const h of card.history) {
          if (h.ts >= todayStart) todayReviews += 1;
          if (h.ts >= weekStart) weekReviews += 1;
        }
      }

      setStats({ totalCards: cards.length, totalReviews, byType, todayReviews, weekReviews });
    })();
  }, []);

  const typeLabels: Record<string, string> = { speaking: '口语', writing: '写作', listening: '听力', vocab: '词汇' };

  return (
    <div className="page-container">
      <header className="page-header">
        <h1 className="page-title">统计</h1>
      </header>

      {/* 复习概览 - 紧凑卡片 */}
      <div
        className="card"
        style={{ marginBottom: '16px' }}
      >
        <div className="card-body" style={{ padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>复习次数</span>
            <span style={{ fontSize: '13px', fontWeight: 600 }}>{stats?.totalReviews ?? 0} 次</span>
          </div>
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1, textAlign: 'center', padding: '8px 0', background: 'var(--bg-secondary)' }}>
              <div style={{ fontSize: '20px', fontWeight: 700 }}>{stats?.todayReviews ?? 0}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>今日</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center', padding: '8px 0', background: 'var(--bg-secondary)' }}>
              <div style={{ fontSize: '20px', fontWeight: 700 }}>{stats?.weekReviews ?? 0}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>本周</div>
            </div>
          </div>
        </div>
      </div>

      {/* 卡片库 */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-body" style={{ padding: 0 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>卡片库</span>
            <span style={{ fontSize: '13px', fontWeight: 600 }}>{stats?.totalCards ?? 0} 张</span>
          </div>
          {['speaking', 'writing', 'listening', 'vocab'].map((type, i) => (
            <div
              key={type}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 16px',
                borderBottom: i < 3 ? '1px solid var(--border-color)' : 'none',
              }}
            >
              <div className="flex items-center gap-sm">
                <span className={`skill-dot ${type}`} />
                <span style={{ fontSize: '13px' }}>{typeLabels[type]}</span>
              </div>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                {stats?.byType[type]?.cards ?? 0}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 历史记录入口 */}
      <div
        className="card"
        style={{ cursor: 'pointer' }}
        onClick={() => navigate('/history')}
      >
        <div className="card-body" style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="flex items-center gap-sm">
            <span style={{ fontSize: '16px' }}>📜</span>
            <span style={{ fontSize: '14px' }}>学习历史</span>
          </div>
          <span style={{ color: 'var(--text-muted)' }}>→</span>
        </div>
      </div>
    </div>
  );
}

function SettingsPage() {
  const [syncState, setSyncState] = useState<{
    syncing: boolean;
    lastSync: Date | null;
    status: 'success' | 'failed' | 'never';
    deviceId: string;
    message?: string;
  }>({
    syncing: false,
    lastSync: null,
    status: 'never',
    deviceId: '',
  });

  const [showDeviceManager, setShowDeviceManager] = useState(false);
  const [editDeviceId, setEditDeviceId] = useState('');
  const [modelMode, setModelModeState] = useState<'fast' | 'quality'>('quality');
  const [appVersion, setAppVersion] = useState('1.0.0');

  const fileInputRef = useRef<HTMLInputElement>(null);

  // 加载同步状态和模型设置
  useEffect(() => {
    import('./sync').then(({ getSyncInfo, getDeviceId }) => {
      const info = getSyncInfo();
      setSyncState(prev => ({
        ...prev,
        lastSync: info.lastSync,
        status: info.status,
        deviceId: getDeviceId(),
      }));
    });
    
    // 加载模型设置
    import('./services/llm/config').then(({ readModelMode }) => {
      setModelModeState(readModelMode());
    });

    // 加载版本号
    fetch('/version.json')
      .then(res => res.json())
      .then(data => setAppVersion(data.version || '1.0.0'))
      .catch(() => {});
  }, []);

  // 切换模型
  const handleModelChange = async (mode: 'fast' | 'quality') => {
    const { setModelMode } = await import('./services/llm/config');
    setModelMode(mode);
    setModelModeState(mode);
  };

  // 执行同步
  const handleSync = async () => {
    setSyncState(prev => ({ ...prev, syncing: true, message: undefined }));
    try {
      const { performSync } = await import('./sync');
      const result = await performSync();

      // 构建更友好的同步消息
      let syncMessage = '同步完成';
      const uploaded = result.uploaded ?? 0;
      const downloaded = result.downloaded ?? 0;
      if (uploaded > 0 || downloaded > 0) {
        const parts = [];
        if (uploaded > 0) parts.push(`上传 ${uploaded}`);
        if (downloaded > 0) parts.push(`下载 ${downloaded}`);
        syncMessage = `同步成功：${parts.join('，')}`;
      } else {
        syncMessage = '已同步，数据无变化';
      }

      setSyncState(prev => ({
        ...prev,
        syncing: false,
        lastSync: result.success ? new Date() : prev.lastSync,
        status: result.success ? 'success' : 'failed',
        message: result.success ? syncMessage : result.error,
      }));
    } catch (error) {
      setSyncState(prev => ({
        ...prev,
        syncing: false,
        status: 'failed',
        message: error instanceof Error ? error.message : '同步失败',
      }));
    }
  };

  // 导出数据
  const handleExport = async () => {
    try {
      const { exportToFile } = await import('./sync');
      await exportToFile();
    } catch (error) {
      alert('导出失败：' + (error instanceof Error ? error.message : '未知错误'));
    }
  };

  // 导入数据
  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const { importFromFile } = await import('./sync');
      const result = await importFromFile(file);
      if (result.success) {
        alert(`导入成功：恢复了 ${result.downloaded ?? 0} 张卡片的数据`);
      } else {
        alert('导入失败：' + result.error);
      }
    } catch (error) {
      alert('导入失败：' + (error instanceof Error ? error.message : '未知错误'));
    }

    // 清空 input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatLastSync = () => {
    if (!syncState.lastSync) return '从未同步';
    return syncState.lastSync.toLocaleString('zh-CN');
  };

  const getStatusColor = () => {
    switch (syncState.status) {
      case 'success': return 'var(--accent-green)';
      case 'failed': return 'var(--accent-red)';
      default: return 'var(--text-muted)';
    }
  };

  const getStatusText = () => {
    switch (syncState.status) {
      case 'success': return '✓ 已同步';
      case 'failed': return '✗ 同步失败';
      default: return '○ 未同步';
    }
  };

  // 打开设备管理
  const openDeviceManager = () => {
    setEditDeviceId(syncState.deviceId);
    setShowDeviceManager(true);
  };

  // 复制设备 ID
  const copyDeviceId = async () => {
    try {
      await navigator.clipboard.writeText(syncState.deviceId);
      alert('已复制到剪贴板');
    } catch {
      alert('复制失败，请手动复制');
    }
  };

  // 保存新设备 ID（必须是 UUID v4 格式）
  const saveDeviceId = async () => {
    const newId = editDeviceId.trim();
    if (!newId) {
      alert('设备 ID 不能为空');
      return;
    }
    
    try {
      const { setDeviceId } = await import('./sync');
      const success = setDeviceId(newId);
      if (!success) {
        alert('设备 ID 格式无效\n\n请使用有效的 UUID 格式，例如：\n6a47bb7b-efc8-4af7-99fa-d4f4e6421b7a');
        return;
      }
      setSyncState(prev => ({ ...prev, deviceId: newId }));
      setShowDeviceManager(false);
      alert('设备 ID 已更新，请点击同步获取该设备的数据');
    } catch (error) {
      alert('保存失败：' + (error instanceof Error ? error.message : '未知错误'));
    }
  };

  // 生成新设备 ID
  const generateNewDeviceId = async () => {
    if (!confirm('生成新 ID 后，当前设备将与云端数据断开关联。确定要继续吗？')) {
      return;
    }
    const { generateDeviceId, setDeviceId } = await import('./sync');
    const newId = generateDeviceId();
    setDeviceId(newId);
    setSyncState(prev => ({ ...prev, deviceId: newId }));
    setEditDeviceId(newId);
    alert('已生成新设备 ID');
  };

  // 清除云端数据
  const deleteCloudData = async () => {
    if (!syncState.deviceId) {
      alert('没有设备 ID');
      return;
    }
    if (!confirm('确定要删除云端数据吗？此操作不可恢复！\n\n本地数据不会受影响。')) {
      return;
    }
    if (!confirm('再次确认：删除后无法恢复，确定继续？')) {
      return;
    }
    try {
      const { deleteFromCloud } = await import('./sync');
      const result = await deleteFromCloud();
      if (result.success) {
        alert('云端数据已删除');
        setSyncState(prev => ({ ...prev, status: 'never', lastSync: null }));
      } else {
        alert('删除失败：' + result.error);
      }
    } catch (error) {
      alert('删除失败：' + (error instanceof Error ? error.message : '未知错误'));
    }
  };

  // 清除缓存（不删除用户数据）
  const clearAppCache = async () => {
    if (!confirm('确定要清除缓存并刷新页面吗？\n\n不会删除任何学习数据。')) {
      return;
    }

    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(reg => reg.unregister()));
      }

      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(key => caches.delete(key)));
      }

      alert('缓存已清除，页面将刷新。');
      window.location.reload();
    } catch (error) {
      alert('清除缓存失败：' + (error instanceof Error ? error.message : '未知错误'));
    }
  };

  // 清除本地数据
  const deleteLocalData = async () => {
    // 第一步：提示导出
    const shouldExport = confirm(
      '⚠️ 警告：完全重置\n\n' +
      '此操作将删除本设备上的所有数据，包括：\n' +
      '• 所有卡片的学习进度和历史记录\n' +
      '• 周学习目标和时长统计\n' +
      '• 本地设置\n\n' +
      '重置后将恢复到全新安装状态。\n\n' +
      '建议先导出备份！\n\n' +
      '点击「确定」先导出备份，点击「取消」直接继续删除。'
    );

    if (shouldExport) {
      try {
        const { exportToFile } = await import('./sync');
        await exportToFile();
        alert('备份已导出。如果确认要重置，请再次点击「完全重置」按钮。');
        return; // 导出后不继续删除，让用户确认备份后再操作
      } catch (error) {
        alert('导出失败：' + (error instanceof Error ? error.message : '未知错误'));
        return;
      }
    }

    // 第二步：再次确认
    if (!confirm('⚠️ 最后确认\n\n确定要完全重置所有本地数据吗？\n\n此操作不可恢复！')) {
      return;
    }

    // 第三步：输入确认
    const confirmText = prompt('请输入「重置」两个字来确认操作：');
    if (confirmText !== '重置') {
      alert('操作已取消');
      return;
    }

    // 第四步：询问是否清除云端关联
    const clearCloudLink = confirm(
      '是否同时断开云端同步关联？\n\n' +
      '• 点击「确定」：生成新设备 ID，不再同步到原有云端数据\n' +
      '• 点击「取消」：保留设备 ID，下次同步会恢复云端数据'
    );

    try {
      // 清除所有 IndexedDB 表
      const { defaultDb } = await import('./db/indexedDb');
      await defaultDb.cards.clear();
      await defaultDb.snapshots.clear();
      await defaultDb.settings.clear();
      await defaultDb.studyLogs.clear();

      // 清除 localStorage 中的同步状态
      localStorage.removeItem('enpass_sync_state');
      localStorage.removeItem('enpass_sync_version');

      // 如果用户选择断开云端关联，清除设备 ID
      if (clearCloudLink) {
        localStorage.removeItem('enpass_device_id');
      }

      alert('已完全重置。页面将刷新。');
      window.location.reload();
    } catch (error) {
      alert('重置失败：' + (error instanceof Error ? error.message : '未知错误'));
    }
  };

  return (
    <div className="page-container">
      <header className="page-header">
        <h1 className="page-title">设置</h1>
        <p className="page-subtitle">Configure your experience</p>
      </header>

      <div className="page-content">
      {/* 数据同步 */}
      <section className="section">
        <h2 className="section-title">DATA SYNC</h2>
        <div className="card">
          <div className="card-body stack">
            {/* 同步按钮 */}
            <button 
              className="btn btn-primary" 
              onClick={handleSync}
              disabled={syncState.syncing}
              style={{ width: '100%' }}
            >
              {syncState.syncing ? '同步中...' : 'SYNC NOW'}
            </button>

            {/* 同步消息 */}
            {syncState.message && (
              <div 
                style={{ 
                  background: syncState.status === 'success' ? 'var(--accent-green)' : 'var(--accent-red)',
                  color: '#000',
                  padding: '12px',
                  border: '2px solid var(--border-color)',
                }}
              >
                {syncState.message}
              </div>
            )}

            {/* 状态信息 */}
            <div className="caption" style={{ color: 'var(--text-secondary)' }}>
              <span style={{ color: getStatusColor(), fontWeight: 700 }}>
                {getStatusText()}
              </span>
              {syncState.lastSync && (
                <span> · {formatLastSync()}</span>
              )}
              {!syncState.lastSync && syncState.status === 'never' && (
                <span> · 从未同步</span>
              )}
            </div>

            {/* 设备 ID */}
            <div 
              className="caption" 
              style={{ 
                color: 'var(--text-muted)', 
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onClick={openDeviceManager}
            >
              设备: {syncState.deviceId ? syncState.deviceId.slice(0, 8) + '...' : '加载中'} →
            </div>
          </div>
        </div>
      </section>

      {/* 设备管理模态框 */}
      {showDeviceManager && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowDeviceManager(false)}
        >
          <div 
            className="card"
            style={{ 
              width: '90%',
              maxWidth: '400px',
              margin: '20px',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="card-body stack">
              <h3 style={{ margin: 0 }}>跨设备同步</h3>
              
              {/* 说明 */}
              <div style={{ 
                background: 'var(--bg-secondary)', 
                padding: '12px', 
                border: '1px solid var(--border-color)',
                fontSize: '13px',
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>想在新设备上继续学习？</strong>
                </div>
                <div>1. 在旧设备复制下方 ID</div>
                <div>2. 在新设备粘贴并点击「使用」</div>
                <div>3. 点击「同步」获取数据</div>
              </div>

              {/* 当前设备 ID */}
              <div>
                <div className="label">本机 ID</div>
                <div 
                  style={{ 
                    fontFamily: 'monospace', 
                    fontSize: '12px',
                    background: 'var(--bg-secondary)',
                    padding: '10px 12px',
                    border: '1px solid var(--border-color)',
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                    marginBottom: '8px',
                  }}
                >
                  {syncState.deviceId}
                </div>
                <button 
                  className="btn btn-secondary" 
                  onClick={copyDeviceId}
                  style={{ width: '100%' }}
                >
                  复制 ID
                </button>
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '4px 0' }} />

              {/* 输入新设备 ID */}
              <div>
                <div className="label">使用其他设备的 ID</div>
                <input
                  type="text"
                  value={editDeviceId}
                  onChange={e => setEditDeviceId(e.target.value)}
                  placeholder="粘贴其他设备的 ID"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    border: '2px solid var(--border-color)',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    boxSizing: 'border-box',
                    marginBottom: '8px',
                  }}
                />
                <button 
                  className="btn btn-primary" 
                  onClick={saveDeviceId}
                  disabled={!editDeviceId.trim() || editDeviceId === syncState.deviceId}
                  style={{ width: '100%' }}
                >
                  使用此 ID
                </button>
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button 
                  className="btn btn-secondary flex-1" 
                  onClick={generateNewDeviceId}
                >
                  重新开始（新 ID）
                </button>
                <button 
                  className="btn btn-secondary flex-1" 
                  onClick={() => setShowDeviceManager(false)}
                >
                  关闭
                </button>
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '12px 0 8px' }} />

              {/* 危险操作 */}
              <button 
                onClick={deleteCloudData}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'transparent',
                  border: '1px dashed var(--accent-red)',
                  color: 'var(--accent-red)',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                清除云端数据
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="section">
        <h2 className="section-title">CACHE</h2>
        <div className="card">
          <div className="card-body stack">
            <p className="caption">清除 PWA/静态资源缓存，不会删除学习数据</p>
            <button className="btn btn-outline" onClick={clearAppCache} style={{ width: '100%' }}>
              清除缓存并刷新
            </button>
          </div>
        </div>
      </section>

      {/* 数据备份 */}
      <section className="section">
        <h2 className="section-title">BACKUP</h2>
        <div className="card">
          <div className="card-body stack">
            <div className="flex gap-md">
              <button className="btn btn-secondary flex-1" onClick={handleExport}>
                导出备份
              </button>
              <button 
                className="btn btn-secondary flex-1" 
                onClick={() => fileInputRef.current?.click()}
              >
                导入备份
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                onChange={handleImport}
              />
            </div>
            <p className="caption">
              导出会生成一个 JSON 文件，包含所有学习进度和历史记录。可用于手动迁移或备份。
            </p>
          </div>
        </div>
      </section>

      {/* SRS 学习管理 */}
      <SrsSettingsSection />

      {/* AI 模型 */}
      <section className="section">
        <h2 className="section-title">AI MODEL</h2>
        <div className="card">
          <div className="card-body stack">
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleModelChange('fast')}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: modelMode === 'fast' ? 'var(--accent-cyan)' : 'var(--bg-card)',
                  border: '2px solid var(--border-color)',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                极速
              </button>
              <button
                onClick={() => handleModelChange('quality')}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: modelMode === 'quality' ? 'var(--accent-cyan)' : 'var(--bg-card)',
                  border: '2px solid var(--border-color)',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                精准
              </button>
            </div>
            <p className="caption">
              {modelMode === 'fast' 
                ? '响应 <1秒，适合快速练习'
                : '响应约2-3秒，更准确的表达建议'}
            </p>
          </div>
        </div>
      </section>

      {/* 危险操作 */}
      <section className="section">
        <h2 className="section-title" style={{ color: 'var(--accent-red)' }}>DANGER ZONE</h2>
        <div className="card" style={{ borderColor: 'var(--accent-red)' }}>
          <div className="card-body stack">
            <button 
              onClick={deleteLocalData}
              style={{
                width: '100%',
                padding: '12px',
                background: 'transparent',
                border: '2px solid var(--accent-red)',
                color: 'var(--accent-red)',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              完全重置
            </button>
            <p className="caption">
              删除本设备上所有数据（学习进度、设置、时长统计），恢复到全新状态。操作前会提示导出备份。
            </p>
          </div>
        </div>
      </section>

      {/* 关于 */}
      <section className="section">
        <h2 className="section-title">ABOUT</h2>
        <div className="card">
          <div className="card-body">
            <div className="label">版本</div>
            <div className="body">enPASS v{appVersion}</div>
          </div>
        </div>
      </section>
      </div>{/* page-content */}
    </div>
  );
}

function App() {
  // 邀请码验证状态
  const [inviteVerified, setInviteVerified] = useState(() => {
    // 如果不需要邀请码，直接通过
    if (!needsInviteCode) return true;
    // 否则检查是否已有有效邀请码
    return hasValidInviteCode();
  });

  // 如果需要邀请码且未验证，显示邀请码门禁
  if (!inviteVerified) {
    return <InviteGate onSuccess={() => setInviteVerified(true)} />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<Navigate to="/today" replace />} />
          <Route path="/today" element={<TodayPage />} />
          <Route path="/stats" element={<StatsPage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/history/:cardId/:entryIndex" element={<HistoryDetailPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/goal" element={<WeeklyGoalPage />} />
          <Route path="/goal/legacy" element={<GoalPlannerPage />} />
          <Route path="/session" element={<SessionPage />} />
          <Route path="/session/:type" element={<SessionPage />} />
          <Route path="/ai-writing" element={<AIWritingPage />} />
          {/* Topic Dictation Module */}
          <Route path="/writing/topic-dictation" element={<TopicDictationPage />} />
          {/* Shadow Reading Module */}
          <Route path="/speaking/shadow" element={<ShadowReadingPage />} />
          <Route path="/speaking/shadow/:id" element={<ShadowPracticePage />} />
          <Route path="/speaking/shadow/:id/retelling" element={<RetellingPage />} />
          <Route path="/speaking/dialog" element={<AIDialogPage />} />
          <Route path="/speaking/dialog/:sessionId" element={<AIDialogPage />} />
          <Route path="*" element={<Navigate to="/today" replace />} />
        </Route>
      </Routes>
      <PWAInstallPrompt />
      <UpdatePrompt />
      <NetworkErrorToast />
    </BrowserRouter>
  );
}

export default App;
